<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import dayjs from '@/helpers/dayjs';
import { RelationshipStatus, RelationshipType, YesNoOptions } from '@/helpers/const';
import { capitalise } from '@/helpers/format';
import { useRelationshipManager } from '@/helpers/useRelationshipManager';
import { forceScrollToTop } from '@/helpers/forceScrollToTop';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { applicantBrokerQuoteSchema } from '@/validation';
import { applicantBrokerQuoteSimpleSchema } from '@/validation/schemas/simple/applicant';
import type { Applicant, ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';

const props = withDefaults(
  defineProps<{
    type: ApplicantTypeEnum;
    modelValue: ApplicantEntry;
    primary?: ApplicantEntry;
    allApplicants?: ApplicantEntry[];
    currentId?: string;
  }>(),
  {
    primary: undefined,
    allApplicants: () => [],
    currentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ApplicantEntry): void;
  (e: 'relationship-change', applicantId: string, newStatus: string, oldStatus?: string): void;
}>();

const overFiftyFiveSelection = ref<'yes' | 'no' | undefined>();
const applicantsRef = ref<ApplicantEntry[]>(props.allApplicants || []);
const isSyncingRelationship = ref(false); // Flag to prevent emit loop during sync
const isSyncingFromLoad = ref(false); // Flag to prevent overFiftyFiveSelection watcher firing during initial load
const { getRelationshipOptions, validate: relationshipValidate } = useRelationshipManager(applicantsRef);
const relationshipOptions = computed(() => getRelationshipOptions(props.modelValue.id));

watch(
  () => props.allApplicants,
  (newApplicants) => {
    applicantsRef.value = newApplicants || [];
  },
  { immediate: true, deep: true },
);

// Sync when *this* applicant's relationshipStatus in the shared list changes (e.g. partner edited).
// A deep watch on the whole list runs on every applicant's mutation; handleRelationshipChange applies
// partner updates before the actor, so the actor's form could briefly see stale array data and revert.
watch(
  () => applicantsRef.value.find((a) => a.id === props.currentId)?.relationshipStatus,
  async (statusInList) => {
    if (!statusInList || statusInList === values.relationshipStatus) return;
    isSyncingRelationship.value = true;
    setFieldValue('relationshipStatus', statusInList as typeof values.relationshipStatus);
    await nextTick();
    isSyncingRelationship.value = false;
  },
);

const relationshipErrors = computed(() => {
  return relationshipValidate.value.filter((error) => error.includes(`[${props.modelValue.id}]`));
});

const { sectionStates } = useUserQuoteProcess();

const applicantDetailsType = computed(() => sectionStates.value.applicantDetails);

const validationSchema = computed(() => {
  if (applicantDetailsType.value === 'simple') {
    return applicantBrokerQuoteSimpleSchema();
  } else {
    return applicantBrokerQuoteSchema();
  }
});

const {
  meta,
  submitCount,
  values,
  handleSubmit,
  validate: validateForm,
  setFieldTouched,
  resetForm,
  setFieldValue,
} = useForm<Applicant>({
  validationSchema: validationSchema,
  validateOnMount: false,
});

watch(
  () => props.modelValue,
  async (applicant, oldApplicant) => {
    // For subsequent updates (not initial load), only update relationshipStatus
    // This preserves form values that haven't been saved yet
    if (oldApplicant) {
      if (applicant.relationshipStatus !== oldApplicant.relationshipStatus) {
        setFieldValue('relationshipStatus', applicant.relationshipStatus as typeof values.relationshipStatus);
      }
      return;
    }
    // Initial load - set all fields; guard the overFiftyFiveSelection watcher so it
    // doesn't clear the saved birthdate while we're restoring form state
    isSyncingFromLoad.value = true;
    const formValues = { ...applicant, type: props.type } as Applicant;
    resetForm({ values: formValues });
    if (applicant?.birthdate) {
      const age = dayjs().diff(applicant.birthdate, 'year');
      overFiftyFiveSelection.value = age > 55 ? 'yes' : 'no';
    } else {
      overFiftyFiveSelection.value = undefined;
    }
    await nextTick();
    isSyncingFromLoad.value = false;
  },
  { immediate: true, deep: true },
);

watch(applicantDetailsType, (newType, oldType) => {
  if (newType === 'comprehensive' && oldType === 'simple') {
    setFieldValue('firstName', '');
    setFieldValue('lastName', '');
  }
});

// Watch name fields and sync to parent so relationship dropdown shows correct names
watch(
  () => [values.firstName, values.lastName],
  ([firstName, lastName]) => {
    if (firstName !== props.modelValue.firstName || lastName !== props.modelValue.lastName) {
      emit('update:modelValue', {
        ...props.modelValue,
        firstName,
        lastName,
      });
    }
  },
);

watch(
  () => values.relationshipToPrimary,
  (val) => {
    if (val === RelationshipType.MARRIED_OR_DEFACTO) {
      setFieldValue('relationshipStatus', RelationshipStatus.MARRIED_OR_DEFACTO);
    }
  },
);

// Emit relationship-change when relationshipStatus changes so parent can sync partner
// Skip emission when change is from sync watcher to prevent loops
watch(
  () => values.relationshipStatus,
  (newStatus, oldStatus) => {
    if (newStatus && newStatus !== oldStatus && !isSyncingRelationship.value) {
      emit('relationship-change', props.currentId, newStatus, oldStatus);
    }
  },
);

watch(
  values,
  (newVals) => {
    emit('update:modelValue', {
      ...newVals,
    });
  },
  { deep: true },
);

watch(overFiftyFiveSelection, (selection) => {
  if (!selection || isSyncingFromLoad.value) return;

  if (applicantDetailsType.value === 'simple') {
    if (selection === 'yes') {
      // Clear so the date input appears blank for the user to enter their own DOB
      setFieldValue('birthdate', '');
    } else if (selection === 'no') {
      // Date input is hidden when 'no'; set a default so the hidden field passes validation
      setFieldValue('birthdate', dayjs().subtract(30, 'year').format('YYYY-MM-DD'));
    }
  }
});

const handleBirthdateBlur = () => {
  const birthDate = dayjs(values.birthdate);
  if (!values.birthdate || !birthDate.isValid()) {
    return;
  }

  const age = dayjs().diff(birthDate, 'year');
  overFiftyFiveSelection.value = age > 55 ? 'yes' : 'no';
};

const update = handleSubmit(
  (values) => {
    emit('update:modelValue', values);

    return true;
  },
  () => false,
);

const cancel = () => {
  addAttempted.value = false;
  resetForm();
  forceScrollToTop(0);
};

/**
 * Validation for "Add another applicant".
 * - All fields must pass EXCEPT when relationshipStatus is 'married_to_applicant' —
 *   that specific error is non-blocking because the user is resolving it by adding another applicant.
 * - An empty/missing relationshipStatus still blocks (the required error is preserved).
 * - Non-relationship errors are made visible by marking those fields as touched,
 *   so BxField renders them without incrementing submitCount.
 */
const addAttempted = ref(false);

const validateForAdd = async () => {
  const result = await validateForm();
  if (result.valid) {
    addAttempted.value = false;
    return true;
  }

  const blockingFields = Object.keys(result.errors).filter(
    (field) => !(field === 'relationshipStatus' && (values.relationshipStatus as string) === 'married_to_applicant'),
  );

  // Make blocking-field errors visible without a submit (touch each field individually)
  blockingFields.forEach((field) => setFieldTouched(field as Parameters<typeof setFieldTouched>[0], true));

  addAttempted.value = blockingFields.length > 0;
  return blockingFields.length === 0;
};

defineExpose({ update, cancel, resetForm, setFieldValue, validateForAdd });
</script>

<template>
  <form>
    <div class="form-group">
      <template v-if="applicantDetailsType === 'comprehensive'">
        <BxField v-slot="{ field }" name="firstName">
          <BxInput
            :id="values.id + '-first-name'"
            :data-test-id="`applicant-${values.id}-first-name`"
            v-bind="field"
            class="form-control"
            label="First name"
            label-type="floating"
          />
        </BxField>
        <BxField v-slot="{ field }" name="lastName">
          <BxInput
            :id="values.id + '-last-name'"
            :data-test-id="`applicant-${values.id}-last-name`"
            v-bind="field"
            class="form-control"
            label="Last name"
            label-type="floating"
            @blur="values.lastName = capitalise(values.lastName)"
          />
        </BxField>
      </template>
      <BxField v-slot="{ field }" name="relationshipStatus">
        <BxSelect
          :id="values.id + '-relationship-status'"
          :data-test-id="`applicant-${values.id}-relationship-status`"
          v-bind="field"
          class="form-control mt-5"
          :list-items="relationshipOptions"
          label="Relationship status"
          label-type="floating"
          :error-message="
            submitCount > 0
              ? relationshipErrors.length
                ? relationshipErrors.map((e) => e.replace(/^\[[^\]]+\]\s*/, '')).join('. ')
                : field.errorMessage
              : field.errorMessage
          "
        />
      </BxField>
      <BxField v-if="applicantDetailsType === 'simple'" v-slot="{ field }" name="birthdate">
        <BxSelect
          :id="values.id + '-over-fifty-five'"
          v-bind="{ ...field, onFocusout: undefined }"
          v-model="overFiftyFiveSelection"
          :data-test-id="`applicant-${values.id}-over-fifty-five`"
          class="form-control"
          :list-items="YesNoOptions"
          label="Over 55?"
          label-type="floating"
          :error-message="overFiftyFiveSelection ? undefined : field.errorMessage"
        />
      </BxField>
      <BxField
        v-if="applicantDetailsType === 'comprehensive' || overFiftyFiveSelection === 'yes'"
        v-slot="{ field }"
        name="birthdate"
      >
        <BxDateInput
          :id="values.id + '-date-of-birth'"
          :data-test-id="`applicant-${values.id}-date-of-birth`"
          v-bind="{ ...field, onFocusout: undefined }"
          class="form-control"
          label="Date of birth"
          label-type="floating"
          placeholder="DD/MM/YYYY"
          @blur="handleBirthdateBlur"
        />
      </BxField>
      <BxErrorPanel
        v-show="!meta.valid && (submitCount > 0 || addAttempted)"
        :data-test-id="`applicant-${values.id}-error-panel`"
        class="mt-6"
      >
        Please review the error messages above.
      </BxErrorPanel>
    </div>
  </form>
</template>
